import { EraQL } from '@cotera/era';

/**
 * Extract symbol references from an eraql string.
 * Finds all symbol tokens that start with '%' (which indicates a symbol reference).
 *
 * @param eraql - The eraql string to extract references from
 * @returns Array of unique symbol names (without the % prefix)
 */
export const extractSymbolReferencesFromEraql = (eraql: string): string[] => {
  const refs: string[] = [];

  // Ensure eraql is a non-empty string before lexing
  if (eraql == null || typeof eraql !== 'string' || eraql.trim().length === 0) {
    return refs;
  }

  // Wrap lexer call in try-catch to handle any unexpected errors
  try {
    const tokens = Array.from(EraQL.lex(eraql));

    for (let i = 0; i < tokens.length; i++) {
      const tokenWithMeta = tokens[i];
      if (!tokenWithMeta) {
        continue;
      }
      const [token, meta] = tokenWithMeta;

      if (token.t === 'symbol' && meta.range) {
        const tokenText = eraql.slice(meta.range[0], meta.range[1]);

        // Check if this is a symbol reference (starts with %)
        if (tokenText.startsWith('%')) {
          let symbolName: string | undefined;

          // Handle quoted format: %"symbol name" -> symbol name
          // For quoted symbols, the lexer tokenizes % and "symbol name" separately
          if (tokenText === '%' && i + 1 < tokens.length) {
            const nextTokenWithMeta = tokens[i + 1];
            if (nextTokenWithMeta) {
              const [nextToken, nextMeta] = nextTokenWithMeta;
              if (nextToken.t === 'ident' && nextMeta.range) {
                const nextTokenText = eraql.slice(
                  nextMeta.range[0],
                  nextMeta.range[1]
                );
                // Check if it's a quoted ident
                if (
                  nextTokenText.startsWith('"') &&
                  nextTokenText.endsWith('"')
                ) {
                  // Extract the symbol name from the quoted ident
                  symbolName = nextTokenText.slice(1, -1); // Remove quotes
                  i++; // Skip the next token since we've processed it
                }
              }
            }
          }

          // Handle unquoted format: %symbol
          if (!symbolName) {
            symbolName = token.sym;
            // Remove % prefix if present (token.sym might be "%symbol" or "symbol")
            if (symbolName.startsWith('%')) {
              symbolName = symbolName.slice(1);
            }
          }

          // Only add if it's a valid symbol reference (not a keyword or built-in)
          if (symbolName && !refs.includes(symbolName)) {
            refs.push(symbolName);
          }
        }
      }
    }
  } catch (error) {
    // If lexing fails, return empty array
    return refs;
  }

  return refs;
};

/**
 * Extract column references from an eraql string.
 * Finds all quoted attribute references (column names) in the expression.
 * Unquoted idents are not extracted as they could be function names or keywords.
 *
 * @param eraql - The eraql string to extract references from
 * @returns Array of unique column names (from quoted references like "columnName")
 */
export const extractColumnReferencesFromEraql = (eraql: string): string[] => {
  const refs: string[] = [];

  // Ensure eraql is a non-empty string before lexing
  if (eraql == null || typeof eraql !== 'string' || eraql.trim().length === 0) {
    return refs;
  }

  // Wrap lexer call in try-catch to handle any unexpected errors
  try {
    const tokens = Array.from(EraQL.lex(eraql));

    for (let i = 0; i < tokens.length; i++) {
      const tokenWithMeta = tokens[i];
      if (!tokenWithMeta) {
        continue;
      }
      const [token, meta] = tokenWithMeta;

      // Look for ident tokens which represent attribute/column references
      if (token.t === 'ident' && meta.range) {
        const tokenText = eraql.slice(meta.range[0], meta.range[1]);

        // Only extract quoted idents as column references (e.g., "columnName")
        // Unquoted idents could be function names, keywords, or other constructs
        if (tokenText.startsWith('"') && tokenText.endsWith('"')) {
          const columnName = tokenText.slice(1, -1); // Remove quotes
          if (columnName && !refs.includes(columnName)) {
            refs.push(columnName);
          }
        }
      }
    }
  } catch (error) {
    // If lexing fails, return empty array
    return refs;
  }

  return refs;
};
